import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Row, Col, Form, InputGroup, ListGroup, Spinner, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import aiChatbotsService from "../../features/ai_chatbots/aiChatbotsService";
import {
  getChatbotSettings,
  updateChatbotSettings,
  listKnowledgeSources,
  createKnowledgeSource,
  ingestTextToSource,
  uploadSourceFile,
  ingestUploadedFile,
  searchPreview,
} from "../../features/ai_chatbots/aiChatbotsService";

const AIChatbotTrainingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const chatbotId = useMemo(() => Number(params.id), [params.id]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [botName, setBotName] = useState("");
  const [settings, setSettings] = useState<any>({
    assistant_profile: { persona: "", tone: "friendly", default_language: "tr", answer_format: "short_paragraphs", max_answer_tokens: 250 },
    retrieval: { top_k: 3, min_score: 0, snippet_chars: 400, chunk_size: 500, chunk_overlap: 60 },
    guardrails: { banned_topics: [], safety_policies: [], no_context_behavior: "ask_clarifying" },
    limits: { max_input_tokens: 1200, max_output_tokens: 300, rate_per_min: 30, cache_ttl_seconds: 3600 },
    provider: { name: "openai", model: "gpt-4o-mini" },
  });

  const [sources, setSources] = useState<any[]>([]);
  const [newSourceTitle, setNewSourceTitle] = useState("");
  const [newSourceLang, setNewSourceLang] = useState("tr");
  const [newText, setNewText] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const bot = await aiChatbotsService.getChatbot(chatbotId);
      setBotName(bot.name);
      const s = await getChatbotSettings(chatbotId);
      if (s && Object.keys(s).length > 0) setSettings((prev: any) => ({ ...prev, ...s }));
      const srcs = await listKnowledgeSources(chatbotId);
      setSources(srcs);
    } catch (e: any) {
      setError(e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chatbotId) {
      loadAll();
    }
  }, [chatbotId]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await updateChatbotSettings(chatbotId, settings);
    } catch (e: any) {
      setError(e?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSource = async () => {
    if (!newSourceTitle.trim()) return;
    const created = await createKnowledgeSource(chatbotId, { type: "text", title: newSourceTitle.trim(), lang: newSourceLang });
    setSources([created, ...sources]);
    setNewSourceTitle("");
    setSelectedSourceId(created.id);
  };

  const handleIngestText = async () => {
    if (!selectedSourceId || !newText.trim()) return;
    await ingestTextToSource(chatbotId, selectedSourceId, newText);
    setNewText("");
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSourceId) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setFileUploading(true);
    try {
      const up = await uploadSourceFile(chatbotId, selectedSourceId, file);
      await ingestUploadedFile(chatbotId, selectedSourceId, up.upload_id);
    } finally {
      setFileUploading(false);
    }
  };

  const handleSearchPreview = async () => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const res = await searchPreview(chatbotId, searchQ.trim());
    setSearchResults(res);
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">{t("AI Chatbot Training")} – {botName}</h3>
        <div>
          <Button variant="secondary" onClick={() => navigate(-1)}>{t("Back")}</Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      <Row>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Persona</Form.Label>
                <Form.Control value={settings.assistant_profile?.persona || ""} onChange={(e) => setSettings({ ...settings, assistant_profile: { ...settings.assistant_profile, persona: e.target.value } })} />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Tone</Form.Label>
                    <Form.Select value={settings.assistant_profile?.tone ?? "friendly"} onChange={(e) => setSettings({ ...settings, assistant_profile: { ...(settings.assistant_profile || {}), tone: e.target.value } })}>
                      <option value="friendly">friendly</option>
                      <option value="professional">professional</option>
                      <option value="casual">casual</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Default Language</Form.Label>
                    <Form.Control value={settings.assistant_profile?.default_language ?? "tr"} onChange={(e) => setSettings({ ...settings, assistant_profile: { ...(settings.assistant_profile || {}), default_language: e.target.value } })} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Answer Format</Form.Label>
                    <Form.Control value={settings.assistant_profile?.answer_format ?? "short_paragraphs"} onChange={(e) => setSettings({ ...settings, assistant_profile: { ...(settings.assistant_profile || {}), answer_format: e.target.value } })} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Max Answer Tokens</Form.Label>
                    <Form.Control type="number" value={settings.assistant_profile?.max_answer_tokens ?? 250} onChange={(e) => setSettings({ ...settings, assistant_profile: { ...(settings.assistant_profile || {}), max_answer_tokens: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Banned Topics (comma-separated)</Form.Label>
                <Form.Control value={(settings.guardrails?.banned_topics || []).join(", ")} onChange={(e) => setSettings({ ...settings, guardrails: { ...settings.guardrails, banned_topics: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })} />
              </Form.Group>
              <div className="d-flex justify-content-end">
                <Button disabled={saving} onClick={handleSaveSettings}>{saving ? t("Saving...") : t("Save Settings")}</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>{t("Retrieval & Limits")}</Card.Header>
            <Card.Body>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Top K</Form.Label>
                    <Form.Control type="number" value={settings.retrieval?.top_k || 3} onChange={(e) => setSettings({ ...settings, retrieval: { ...settings.retrieval, top_k: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Snippet Chars</Form.Label>
                    <Form.Control type="number" value={settings.retrieval?.snippet_chars || 400} onChange={(e) => setSettings({ ...settings, retrieval: { ...settings.retrieval, snippet_chars: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Chunk Size</Form.Label>
                    <Form.Control type="number" value={settings.retrieval?.chunk_size || 500} onChange={(e) => setSettings({ ...settings, retrieval: { ...settings.retrieval, chunk_size: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Chunk Overlap</Form.Label>
                    <Form.Control type="number" value={settings.retrieval?.chunk_overlap || 60} onChange={(e) => setSettings({ ...settings, retrieval: { ...settings.retrieval, chunk_overlap: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Max Output Tokens</Form.Label>
                    <Form.Control type="number" value={settings.limits?.max_output_tokens || 300} onChange={(e) => setSettings({ ...settings, limits: { ...settings.limits, max_output_tokens: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Cache TTL (sec)</Form.Label>
                    <Form.Control type="number" value={settings.limits?.cache_ttl_seconds || 3600} onChange={(e) => setSettings({ ...settings, limits: { ...settings.limits, cache_ttl_seconds: Number(e.target.value) } })} />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button disabled={saving} onClick={handleSaveSettings}>{saving ? t("Saving...") : t("Save Settings")}</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>{t("Knowledge Sources")}</Card.Header>
            <Card.Body>
              <InputGroup className="mb-2">
                <Form.Control placeholder="Source title" value={newSourceTitle} onChange={(e) => setNewSourceTitle(e.target.value)} />
                <Form.Select value={newSourceLang} onChange={(e) => setNewSourceLang(e.target.value)} style={{ maxWidth: 120 }}>
                  <option value="tr">tr</option>
                  <option value="en">en</option>
                </Form.Select>
                <Button onClick={handleCreateSource}>{t("Create Source")}</Button>
              </InputGroup>
              <ListGroup style={{ maxHeight: 220, overflowY: "auto" }}>
                {sources.map((s) => (
                  <ListGroup.Item key={s.id} active={s.id === selectedSourceId} onClick={() => setSelectedSourceId(s.id)} style={{ cursor: "pointer" }}>
                    #{s.id} – {s.title || "(untitled)"} {s.is_active ? "" : "(inactive)"}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>{t("Add Knowledge (Text/File)")}</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>{t("Text Content")}</Form.Label>
                <Form.Control as="textarea" rows={6} value={newText} onChange={(e) => setNewText(e.target.value)} />
              </Form.Group>
              <div className="d-flex gap-2 mb-3">
                <Button variant="primary" disabled={!selectedSourceId || !newText.trim()} onClick={handleIngestText}>{t("Ingest Text")}</Button>
                <Form.Control type="file" onChange={handleUploadFile} disabled={!selectedSourceId || fileUploading} />
              </div>
              {fileUploading && <div className="text-muted">{t("Uploading...")}</div>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12} className="mb-3">
          <Card>
            <Card.Header>{t("Search Preview")}</Card.Header>
            <Card.Body>
              <InputGroup className="mb-2">
                <Form.Control placeholder={t("Your question or keywords") as string} value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
                <Button onClick={handleSearchPreview}>{t("Search")}</Button>
              </InputGroup>
              {searchResults.length === 0 ? (
                <div className="text-muted">{t("No results")}</div>
              ) : (
                <ListGroup>
                  {searchResults.map((r, idx) => (
                    <ListGroup.Item key={idx}>
                      <div className="d-flex justify-content-between"><strong>Score:</strong> <span>{r.score}</span></div>
                      <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{r.snippet}</pre>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AIChatbotTrainingPage;


